import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import type { PopperProps } from '@mui/material/Popper';
import Popper from '@mui/material/Popper';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { tagsActions, tagsSelectors, tagsTypes } from 'app-components/flux/tags';
import type { Tag, TagProps } from 'app-components/flux/tags/tagsTypes';
import { useAppSelector } from 'hooks/redux';
import React, { memo, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

const useStyles = makeStyles()((_theme) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 80,
  },
  chip: {
    height: 24,
  },
  loadingIcon: {
    position: 'absolute',
    right: '5%',
    top: '45%',
  },
}));

const AutoWidthPopper = (props: PopperProps) => <Popper {...props} disablePortal style={{ width: 'max-content' }} />;

export type TagsFieldProps = Omit<AutocompleteProps<Tag | TagProps, true, true, false>, 'renderInput' | 'options'> & {
  options?: Array<Tag | TagProps>;
  slotProps?: { textFieldProps?: TextFieldProps };
};

function TagsField(props: TagsFieldProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { value, onChange, classes: inputClasses, options, slotProps, ...other } = props;
  const { textFieldProps, ...otherSlotProps } = slotProps || {};
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const tagsById = useSelector(tagsSelectors.getTags);
  const goalOptionsFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'goalWithdrawTxns'));

  const filteredTags = useMemo(
    () =>
      tagsById
        .toArray()
        .map((value) => value[1])
        .filter((tag) => !goalOptionsFF || tag.type !== 'GOAL')
        .sort((a, b) =>
          (a?.name?.toLowerCase()?.trim() ?? '\uffff').localeCompare(b?.name?.toLowerCase()?.trim() ?? '\uffff'),
        ),
    [goalOptionsFF, tagsById],
  );

  return (
    <Autocomplete
      multiple
      id='tags-field'
      limitTags={4}
      getOptionLabel={(option) => {
        if (option.id === '-1' && option.name) {
          // the Create 'x' tag
          return option.name;
        }
        if (option.id) {
          return tagsById.get(option.id)?.name || 'Unknown';
        }
        return 'Unknown';
      }}
      classes={{ tag: classes.chip, ...inputClasses }}
      value={value || []}
      onChange={(e, value, reason, details) => {
        if (details && details?.option.id === '-1' && details.option.description) {
          setLoading(true);
          const tagToCreate = tagsTypes.mkTag({
            clientId: uuidv4(),
            name: details.option.description.trim(),
          });
          actionHelpers
            .bindPromiseAction<Tag, any, { data: { resource: Tag } }>(
              dispatch,
              tagsActions.createTag,
            )(tagToCreate)
            .then((resp) => {
              const tagArray = value;
              tagArray.pop();
              tagArray.push(resp.data.resource);
              onChange?.(e, tagArray, reason, details);
              setTimeout(() => {
                // show loading icon so it doesnt look so jumpy
                setLoading(false);
              }, 500);
            })
            .catch(() => setLoading(false));
        } else {
          onChange?.(e, value, reason, details);
        }
      }}
      disableClearable
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.id === value.id}
      className={classes.column}
      filterSelectedOptions
      filterOptions={(options, params) => {
        const filter = createFilterOptions<Tag | TagProps>();
        const filtered = filter(options, params);
        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = tagsById.some((tag) => tag.name?.trim().toLowerCase() === inputValue.trim().toLowerCase());
        if (inputValue !== '' && !isExisting) {
          const tagToCreate = tagsTypes.mkTag({
            id: '-1',
            name: `Create "${inputValue.trim()}"`,
            description: inputValue.trim(),
          });
          filtered.push(tagToCreate);
        }
        return filtered;
      }}
      loading={loading}
      noOptionsText='Tags already applied'
      options={options || filteredTags}
      slotProps={otherSlotProps}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option) => {
        const isAddItem = option.id === '-1';
        return (
          <MenuItem
            {...props}
            key={option.id}
            sx={{
              ...(isAddItem && {
                color: 'primary.main',
              }),
            }}
          >
            {isAddItem ? (
              <ListItemIcon>
                <AddCircleRoundedIcon
                  sx={{
                    color: 'primary.main',
                  }}
                />
              </ListItemIcon>
            ) : null}
            <ListItemText>{option.name}</ListItemText>
          </MenuItem>
        );
      }}
      PopperComponent={AutoWidthPopper}
      {...other}
      renderInput={(params) => {
        return (
          <TextField
            label='Tags'
            {...params}
            {...textFieldProps}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps?.InputProps,
              inputProps: {
                ...params.inputProps,
                ...textFieldProps?.inputProps,
                maxLength: 32,
              },
              endAdornment: loading ? (
                <CircularProgress color='inherit' size={20} className={classes.loadingIcon} />
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        const sortedTags = tagValue
          .map((option) => tagsById.get(option?.id || '')) // Map IDs to full tag objects
          .sort((a, b) => {
            if (a?.type === 'GOAL' && b?.type !== 'GOAL') return -1; // GOAL first
            if (a?.type !== 'GOAL' && b?.type === 'GOAL') return 1; // Other tags after
            return 0; // Preserve original order for same type
          });

        return sortedTags.map((tag, index) => {
          const isGoalTag = goalOptionsFF && tag?.type === 'GOAL'; // Deals with disabling editing and deletion
          return (
            <Chip
              {...getTagProps({ index })}
              key={tag?.id}
              label={tag?.name}
              onDelete={!isGoalTag ? getTagProps({ index }).onDelete : undefined}
              title={isGoalTag ? 'Goal tags cannot be deleted from here' : ''}
              style={{
                cursor: isGoalTag ? 'default' : 'pointer',
                opacity: isGoalTag ? 0.7 : 1,
                backgroundColor: isGoalTag ? theme.palette.inactive.light : undefined,
                color: isGoalTag ? theme.palette.inactive.dark : undefined,
              }}
            />
          );
        });
      }}
    />
  );
}

export default memo(TagsField);
